/**
 * Reading the compact tables of a snapshot. Every list in a section is {cols: [...], rows: [[...], ...]}: rows(table)
 * turns one into objects, index(table, col) into a Map, column(table, col) into one array. Shares and rates are
 * fractions (0,78), dates ISO ('2026-10-21'), months 'YYYY-MM', weeks 'YYYY-Www', distances km.
 */

import type { Table } from "./snapshot";

/** Anything with the compact table shape: a Table of the snapshot types, or a section that is itself a table. */
export interface TableLike<R> {
  readonly cols: readonly string[];
  readonly rows: readonly (readonly unknown[])[];
  readonly __row?: R;
}

const decoded = new WeakMap<object, readonly unknown[]>();

/** A compact table as objects: rows({cols: ['a', 'b'], rows: [[1, 2]]}) is [{a: 1, b: 2}]. Cached per table. */
export function rows<R>(table: TableLike<R> | Table<R> | null | undefined): readonly R[] {
  if (!table) {
    return [];
  }
  const hit = decoded.get(table);
  if (hit) {
    return hit as readonly R[];
  }
  const cols = table.cols;
  const out = Object.freeze(
    (table.rows ?? []).map((r) => Object.freeze(Object.fromEntries(cols.map((c, i) => [c, r[i]])))),
  );
  decoded.set(table, out);
  return out as unknown as readonly R[];
}

/** A compact table as a Map from one column to its row objects: index(bord.cranes, 'crane_id'). */
export function index<R, K extends keyof R>(
  table: TableLike<R> | null | undefined,
  col: K,
): Map<R[K], R> {
  return new Map(rows(table).map((r) => [r[col], r]));
}

/** One column of a compact table as an array. */
export function column<R, K extends keyof R & string>(
  table: TableLike<R> | null | undefined,
  col: K,
): R[K][] {
  const i = table?.cols.indexOf(col) ?? -1;
  return i < 0 || !table ? [] : table.rows.map((r) => r[i] as R[K]);
}
